import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, Center, Flex, Group, Switch, Table, Text } from '@mantine/core';
import { IconDownload, IconUserPlus } from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  patientListApiSlice,
  useGetAllPatientsQuery
} from '@/store/slices/api/patient/patientListApiSlice';
import { useGetUserSettingsQuery } from '@/store/slices/api/userApiSlice';
import {
  clearCurrentReportFilters,
  clearPreviousReportFilters,
  setCurrentPage,
  setCurrentReportFilters,
  setPageLimit,
  setPreviousReportFilters,
  setSearchValue,
  setSort
} from '@/store/slices/patientList/patientListFiltersSlice';
import { usePatientListFeatureFlag } from '@/hooks/usePatientListFeatureFlag';
import { ListPager } from '@/components/ListPager';
import pocStyles from '@/styles/pocScope.module.scss';

import CreatePatientModal from '@/pages/physician/CreatePatient/CreatePatient';
import { PatientData, PatientListRequestParams } from '@/types/patientList';

import { AsyncBatchDownloadModal } from './AsyncBatchDownloadModal';
import { ColumnsButton } from './ColumnsButton';
import CombinedFiltersDropdown from './CombinedFiltersDropdown';
import { MIN_PATIENT_SEARCH_LENGTH } from './constants';
import DebounceSearchInput from './CustomDebounceSearch';
import { FiltersButton } from './FiltersButton';
import { useColumnPreferences } from './hooks/useColumnPreferences';
import { useNotDownloadedReports } from './hooks/useNotDownloadedReports';
import { columnsForEnrollmentMode } from './hooks/useRowChecklist';
import styles from './PatientListPage.module.scss';
import { PatientTableRow } from './PatientTableRow';
import TableHeader from './TableHeader';
import TableSkeleton from './TableSkeleton';

type PreviousReportFilters = {
  colors?: string[];
  isRecentSigned?: true;
  isDownloaded?: true;
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
};

type CurrentReportFilters = {
  colors?: string[];
  isNonPractitionerSigned?: true;
  isPendingReview?: true;
};

type SortDirection = 'ASC' | 'DESC' | null;

// Hoisted to module scope so it has a stable reference and never causes useMemo to recompute
const STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Candidate' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Paused', value: 'Paused' }
] as const;

const PAGE_SIZE_OPTIONS = ['5', '10', '25', '50', '100'];

const PageHeader: React.FC<{
  patientCount?: number;
  isLoading: boolean;
  searchValue: string | undefined;
  onSearchChange: (value: string | undefined) => void;
  notDownloadedCount?: number;
  isNotDownloadedLoading: boolean;
  onNotDownloadedClick: () => void;
  currentReportFilters?: CurrentReportFilters;
  previousReportFilters?: PreviousReportFilters;
  onCurrentReportFilterChange: (
    filters: string[],
    isNonPractitioner: boolean,
    isPendingReview: boolean
  ) => void;
  onPreviousReportFilterChange: (
    filters: string[],
    isRecentSigned?: boolean,
    isDownloaded?: boolean,
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => void;
  selectedReportsCount?: number;
  isPatientCreateEnabled?: boolean;
  onAddPatient?: () => void;
  columnsButton?: React.ReactNode;
  filtersButton?: React.ReactNode;
  isPatientListV2Enabled?: boolean;
  showEnrollmentMode?: boolean;
  enrollmentMode?: boolean;
  onEnrollmentModeChange?: (enabled: boolean) => void;
}> = ({
  patientCount,
  isLoading,
  searchValue,
  onSearchChange,
  notDownloadedCount,
  isNotDownloadedLoading,
  onNotDownloadedClick,
  currentReportFilters,
  previousReportFilters,
  onCurrentReportFilterChange,
  onPreviousReportFilterChange,
  selectedReportsCount,
  isPatientCreateEnabled,
  onAddPatient,
  columnsButton,
  filtersButton,
  isPatientListV2Enabled,
  showEnrollmentMode,
  enrollmentMode,
  onEnrollmentModeChange
}) => {
  const reportFilterProps = {
    selectedCurrentReportFilters: currentReportFilters?.colors || [],
    selectedPreviousReportFilters: previousReportFilters?.colors || [],
    isNonPractitionerSignedCRSelected: currentReportFilters?.isNonPractitionerSigned || false,
    isPendingReviewSelectedCRSelected: currentReportFilters?.isPendingReview || false,
    isRecentSignedSelected: previousReportFilters?.isRecentSigned || false,
    isDownloadedSelected: previousReportFilters?.isDownloaded || false,
    recentSignedSortBy:
      (previousReportFilters?.recentSignedSortBy as 'SIGNED_DATE' | 'DATE_OF_SERVICE') ||
      'SIGNED_DATE',
    currentReportType: (currentReportFilters && Object.keys(currentReportFilters ?? {}).length > 0
      ? 'current'
      : 'previous') as 'current' | 'previous',
    onApplyReportFilters: (
      filters: string[],
      isNonPractitioner: boolean,
      isPendingReview: boolean,
      isRecentSigned: boolean,
      isNotDownloaded: boolean,
      reportType: 'current' | 'previous',
      recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
    ) => {
      if (reportType === 'previous') {
        onPreviousReportFilterChange(filters, isRecentSigned, isNotDownloaded, recentSignedSortBy);
      } else {
        onCurrentReportFilterChange(filters, isNonPractitioner, isPendingReview);
      }
    },
    showNoReportOption: true,
    showPreviousReportOptions: true
  };

  if (!isPatientListV2Enabled) {
    return (
      <div className={styles.headerSection}>
        <div className={styles.titleSection}>
          <Text className={styles.title}>
            Patients&nbsp;
            {!isLoading && patientCount !== undefined && (
              <Text component="span" className={styles.subTitle}>
                {patientCount.toLocaleString()} PATIENTS
              </Text>
            )}
          </Text>
          <DebounceSearchInput
            placeholder="🔍︎    Search patients"
            searchValue={searchValue}
            setSearchValue={onSearchChange}
            minSearchLength={MIN_PATIENT_SEARCH_LENGTH}
            style={{ width: 500, marginLeft: 16 }}
            size="sm"
          />
        </div>
        <Flex>
          <CombinedFiltersDropdown {...reportFilterProps} />
          {isPatientCreateEnabled && (
            <Button leftSection={<IconUserPlus size={16} />} onClick={onAddPatient} ml="md">
              Add Patient
            </Button>
          )}
          <Button
            loading={isNotDownloadedLoading}
            onClick={onNotDownloadedClick}
            disabled={
              selectedReportsCount ? false : !notDownloadedCount || notDownloadedCount === 0
            }
            ml="md">
            <IconDownload size={16} /> &nbsp;&nbsp;
            {selectedReportsCount && selectedReportsCount > 0
              ? 'Previous Reports'
              : 'Not Downloaded'}
            {!isNotDownloadedLoading && (
              <Text component="span">({selectedReportsCount || notDownloadedCount || 0})</Text>
            )}
          </Button>
        </Flex>
      </div>
    );
  }

  // v2: header (title + count + search on the left, actions on the right), then a
  // right-aligned Filters / Columns row.
  return (
    <>
      <div className={styles.headerSectionV2}>
        <div className={styles.titleSectionV2}>
          <Text
            component="h1"
            size="xl"
            fw={700}
            c="plum.8"
            style={{ letterSpacing: '-0.3px', fontSize: 20, lineHeight: 1.2, margin: 0 }}>
            Patients
          </Text>
          {!isLoading && patientCount !== undefined && (
            <span className={pocStyles.pocCount}>{patientCount.toLocaleString()} PATIENTS</span>
          )}
          <DebounceSearchInput
            placeholder="🔍︎    Search patients"
            searchValue={searchValue}
            setSearchValue={onSearchChange}
            minSearchLength={MIN_PATIENT_SEARCH_LENGTH}
            style={{ width: 260, maxWidth: '100%', marginLeft: 16, flex: '0 1 260px' }}
            size="sm"
          />
        </div>

        {/* Add Patient (primary) first, then the download button (secondary) in the same spot
            whether or not Add Patient shows. Same behavior as v1: opens AsyncBatchDownloadModal. */}
        <Flex align="center" gap="sm">
          {isPatientCreateEnabled && (
            <Button
              color="plum.9"
              size="sm"
              h={36}
              radius={8}
              fw={600}
              leftSection={<IconUserPlus size={16} />}
              onClick={onAddPatient}>
              Add Patient
            </Button>
          )}
          <Button
            variant="default"
            size="sm"
            h={36}
            radius={8}
            fw={600}
            leftSection={<IconDownload size={16} />}
            loading={isNotDownloadedLoading}
            onClick={onNotDownloadedClick}
            disabled={
              selectedReportsCount ? false : !notDownloadedCount || notDownloadedCount === 0
            }>
            {selectedReportsCount && selectedReportsCount > 0
              ? 'Previous Reports'
              : 'Not Downloaded'}
            {!isNotDownloadedLoading && (
              <Text component="span" inherit ml={4}>
                ({selectedReportsCount || notDownloadedCount || 0})
              </Text>
            )}
          </Button>
          {/* The global filter (clinics, practitioners, device types, vendors) now lives in the
              Filters button below, so the old FiltersModal icon isn't shown here. */}
        </Flex>
      </div>
      {(filtersButton || columnsButton || showEnrollmentMode) && (
        <Group justify="space-between" py="sm" gap={8} wrap="nowrap">
          {showEnrollmentMode ? (
            <Switch
              color="plum.9"
              size="sm"
              label="Enrollment mode"
              checked={enrollmentMode}
              onChange={(event) => onEnrollmentModeChange?.(event.currentTarget.checked)}
            />
          ) : (
            <span />
          )}
          <Group gap={8} wrap="nowrap">
            {filtersButton}
            {columnsButton}
          </Group>
        </Group>
      )}
    </>
  );
};

const PatientListPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    searchValue,
    previousReportFilters,
    currentReportFilters,
    sortBy,
    sortDirection,
    currentPage,
    pageLimit: rawPageLimit
  } = useAppSelector((state) => state.patientListFilters);

  const { isPatientListV2Enabled } = usePatientListFeatureFlag();
  const {
    columns: defaultColumns,
    setColumnVisibility,
    reorderColumns,
    density,
    setDensity
  } = useColumnPreferences();

  // Initialize columns with user preferences if available
  const { data: userSettingsData } = useGetUserSettingsQuery();
  const [columns, setColumns] = useState(defaultColumns);

  // Seed report filters into Redux from user settings on first load
  // (effective clinics/practitioners/etc. are derived directly below from userSettingsData,
  // avoiding a render-cycle gap)
  useEffect(() => {
    if (!userSettingsData?.patientListPagePreferences) return;
    // Only initialize once — skip if report filters are already populated
    if (hasCurrentFilters || hasPreviousFilters) return;

    const prefs = userSettingsData.patientListPagePreferences;

    if (prefs.currentReportFilters && Object.keys(prefs.currentReportFilters).length > 0) {
      const cf = prefs.currentReportFilters;
      const next: { colors?: string[]; isNonPractitionerSigned?: true; isPendingReview?: true } =
        {};
      if (cf.colors && cf.colors.length > 0) next.colors = cf.colors;
      if (cf.isPendingReview === true) next.isPendingReview = true;
      if (Object.keys(next).length > 0) dispatch(setCurrentReportFilters(next));
    } else if (prefs.previousReportFilters && Object.keys(prefs.previousReportFilters).length > 0) {
      const pf = prefs.previousReportFilters;
      const next: {
        colors?: string[];
        isRecentSigned?: true;
        recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
        isDownloaded?: true;
        isNonPractitionerSigned?: true;
      } = {};
      if (pf.colors && pf.colors.length > 0) next.colors = pf.colors;
      if (pf.isRecentSigned === true) {
        next.isRecentSigned = true;
        if (pf.recentSignedSortBy) next.recentSignedSortBy = pf.recentSignedSortBy;
      }
      if (pf.isDownloaded === true) next.isDownloaded = true;
      if (pf.isNonPractitionerSigned === true) next.isNonPractitionerSigned = true;
      if (Object.keys(next).length > 0) dispatch(setPreviousReportFilters(next));
    }
  }, [userSettingsData, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply user preferences to columns when they become available
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences) {
      const userColumns = userSettingsData.patientListPagePreferences.columns || [];

      // Merge user column preferences with default columns
      const updatedColumns = defaultColumns.map((col) => {
        const userColumn = userColumns.find((uc: any) => uc.id === col.id);
        return {
          ...col,
          visible: userColumn ? userColumn.visible : col.visible
        };
      });

      setColumns(updatedColumns);
    } else {
      // If no user settings, use defaults
      setColumns(defaultColumns);
    }
  }, [userSettingsData]);

  const { user } = useAppSelector((state) => state.auth);
  const isInternalUser = Boolean(user?.role?.includes('internal'));
  const [enrollmentMode, setEnrollmentMode] = useState(false);
  const tableColumns = useMemo(
    () => columnsForEnrollmentMode(columns, isInternalUser && enrollmentMode),
    [columns, enrollmentMode, isInternalUser]
  );
  const isPatientCreateEnabled = user?.organizations?.[0]?.parent?.isCreatePatientInRs;
  const [isCreatePatientOpen, setIsCreatePatientOpen] = useState(false);

  // Default pageLimit to 10 if not present
  const pageLimit = rawPageLimit || 10;
  const skip = (currentPage - 1) * pageLimit;
  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(new Set());
  const [selectedReports, setSelectedReports] = useState<string[]>([]); // accumulates notDownloadedReports

  // Helper functions to check if filters exist
  const hasPreviousFilters = previousReportFilters && Object.keys(previousReportFilters).length > 0;
  const hasCurrentFilters = currentReportFilters && Object.keys(currentReportFilters).length > 0;

  // Derive effective report filters: use Redux values if already seeded,
  // otherwise fall back directly to userSettingsData on first render
  const prefs = userSettingsData?.patientListPagePreferences;

  const effectiveCurrentReportFilters: CurrentReportFilters | undefined = (() => {
    if (hasCurrentFilters) return currentReportFilters;
    if (!prefs?.currentReportFilters) return undefined;
    const cf = prefs.currentReportFilters;
    const next: CurrentReportFilters = {};
    if (cf.colors && cf.colors.length > 0) next.colors = cf.colors;
    if (cf.isPendingReview === true) next.isPendingReview = true;
    return Object.keys(next).length > 0 ? next : undefined;
  })();

  const effectivePreviousReportFilters: PreviousReportFilters | undefined = (() => {
    if (hasPreviousFilters) return previousReportFilters;
    if (effectiveCurrentReportFilters) return undefined; // only one type at a time
    if (!prefs?.previousReportFilters) return undefined;
    const pf = prefs.previousReportFilters;
    const next: PreviousReportFilters = {};
    if (pf.colors && pf.colors.length > 0) next.colors = pf.colors;
    if (pf.isRecentSigned === true) {
      next.isRecentSigned = true;
      if (pf.recentSignedSortBy) next.recentSignedSortBy = pf.recentSignedSortBy;
    }
    if (pf.isDownloaded === true) next.isDownloaded = true;
    if (pf.isNonPractitionerSigned === true) next.isNonPractitionerSigned = true;
    return Object.keys(next).length > 0 ? next : undefined;
  })();

  // Only send one type of filter at a time (API requirement)
  const apiCurrentReportFilters =
    effectiveCurrentReportFilters && !effectivePreviousReportFilters
      ? effectiveCurrentReportFilters
      : undefined;
  const apiPreviousReportFilters =
    effectivePreviousReportFilters && !effectiveCurrentReportFilters
      ? effectivePreviousReportFilters
      : undefined;

  // Wrap all effective* in useMemo so their references are stable across renders.
  // Without this, every render produces new array objects, causing the selection-reset
  // useEffect to fire on every render (infinite loop).
  // NOTE: previously these fell back to a stale `preferencesSlice.filters` ("global filter")
  // Redux snapshot seeded once at login, and even gave it priority over fresher user-settings
  // data. That's removed — userSettingsData (RTK Query, refetched whenever settings are saved)
  // is now the single source of truth here.
  const effectiveClinics = useMemo(
    () => userSettingsData?.filteredClinics?.map((c) => ({ label: c.name, value: c.id })) ?? [],
    [userSettingsData?.filteredClinics]
  );

  const effectivePractitioners = useMemo(
    () =>
      userSettingsData?.filteredPractitioners?.map((p) => ({ label: p.name, value: p.id })) ?? [],
    [userSettingsData?.filteredPractitioners]
  );

  const effectiveDeviceTypes = useMemo(
    () => userSettingsData?.deviceTypes?.map((d) => ({ label: d, value: d })) ?? [],
    [userSettingsData?.deviceTypes]
  );

  const effectiveVendors = useMemo(
    () => userSettingsData?.vendors?.map((v) => ({ label: v, value: v })) ?? [],
    [userSettingsData?.vendors]
  );

  const effectivePatientStatuses = useMemo(() => {
    const savedStatuses =
      userSettingsData?.patientStatuses && userSettingsData.patientStatuses.length > 0
        ? userSettingsData.patientStatuses
        : (userSettingsData?.patientListPagePreferences?.patientStatuses ?? []);
    return STATUS_OPTIONS.filter((o) => savedStatuses.includes(o.value));
  }, [userSettingsData]);

  const trimmedSearch = searchValue?.trim();
  const normalizedSearch =
    trimmedSearch && trimmedSearch.length >= MIN_PATIENT_SEARCH_LENGTH ? trimmedSearch : undefined;

  const patientListParams: PatientListRequestParams = {
    skip,
    pageLimit: pageLimit,
    forV2: true,
    isNew: false,
    status: 'All',
    search: normalizedSearch,
    clinicIds: effectiveClinics.map((clinic) => clinic.value),
    practitionersIds: effectivePractitioners.map((practitioner) => practitioner.value),
    vendors: effectiveVendors.map((vendor) => vendor.value),
    deviceTypes: effectiveDeviceTypes.map((deviceType) => deviceType.value),
    patientStatuses: effectivePatientStatuses.map((status) => status.value),
    currentReportFilters: apiCurrentReportFilters,
    previousReportFilters: apiPreviousReportFilters,
    sortBy: sortBy || undefined,
    sortDirection: sortDirection || undefined
  };
  const patientListParamsRef = useRef(patientListParams);
  patientListParamsRef.current = patientListParams;

  const { data, isLoading, error, isFetching } = useGetAllPatientsQuery(patientListParams);

  const refreshPatientList = useCallback(
    (readFromWriter = false) => {
      const params = patientListParamsRef.current;
      if (!readFromWriter) {
        dispatch(
          patientListApiSlice.endpoints.getAllPatients.initiate(params, {
            forceRefetch: true,
            subscribe: false
          })
        );
        return;
      }

      dispatch(
        patientListApiSlice.endpoints.getAllPatientsFromWriter.initiate(params, {
          forceRefetch: true,
          subscribe: false
        })
      )
        .unwrap()
        .catch(() => {
          dispatch(
            patientListApiSlice.endpoints.getAllPatients.initiate(params, {
              forceRefetch: true,
              subscribe: false
            })
          );
        });
    },
    [dispatch]
  );

  const resetSelection = () => {
    setSelectedPatientIds(new Set());
    setSelectedReports([]);
  };

  const {
    notDownloadedCount,
    isNotDownloadedLoading,
    batchState,
    setBatchState,
    onDownloadAllNotDownloadedReports,
    handleBatchDownload,
    handleClose
  } = useNotDownloadedReports(
    selectedReports.length > 0 ? selectedReports.map(Number) : undefined,
    resetSelection,
    refreshPatientList,
    {
      clinics: effectiveClinics,
      practitioners: effectivePractitioners,
      deviceTypes: effectiveDeviceTypes,
      vendors: effectiveVendors,
      patientStatuses: effectivePatientStatuses,
      currentReportFilters: apiCurrentReportFilters,
      previousReportFilters: apiPreviousReportFilters
    }
  );

  const showTableLoading = isLoading || isFetching;

  // Check if any filters are applied
  const hasActiveSearch = normalizedSearch !== undefined;
  const hasActiveFilters =
    hasActiveSearch ||
    effectiveClinics.length > 0 ||
    effectivePractitioners.length > 0 ||
    effectiveDeviceTypes.length > 0 ||
    effectiveVendors.length > 0 ||
    effectivePatientStatuses.length > 0 ||
    hasPreviousFilters ||
    hasCurrentFilters;

  const currentPatients = data?.patients ?? [];
  // Replace existing isAllSelected and isIndeterminate:
  const eligiblePatients = currentPatients.filter((p) => (p.notDownloadedReports?.length ?? 0) > 0);
  const isAllSelected =
    eligiblePatients.length > 0 && eligiblePatients.every((p) => selectedPatientIds.has(p.id));
  const isIndeterminate =
    !isAllSelected && eligiblePatients.some((p) => selectedPatientIds.has(p.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newIds = new Set(selectedPatientIds);
      const newReports = [...selectedReports];
      currentPatients.forEach((patient) => {
        // Skip patients with no previous reports
        if (!patient.previousReport) return;

        if (!newIds.has(patient.id)) {
          newIds.add(patient.id);
          newReports.push(patient.previousReport.id); // Add previous report ID
        }
      });
      setSelectedPatientIds(newIds);
      setSelectedReports(newReports);
    } else {
      const deselectedIds = new Set(currentPatients.map((p) => p.id));
      const deselectedReports = new Set(
        currentPatients.map((p) => p.previousReport?.id).filter(Boolean) // Collect previous report IDs
      );
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        deselectedIds.forEach((id) => next.delete(id));
        return next;
      });
      setSelectedReports((prev) => prev.filter((r) => !deselectedReports.has(r)));
    }
  };

  const handleTogglePatient = (patient: PatientData) => {
    const isCurrentlySelected = selectedPatientIds.has(patient.id);
    if (isCurrentlySelected) {
      // Deselect: remove id and their previous report
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        next.delete(patient.id);
        return next;
      });
      setSelectedReports((prev) => prev.filter((r) => r !== patient.previousReport?.id));
    } else {
      // Select: add id and their previous report
      setSelectedPatientIds((prev) => new Set(prev).add(patient.id));
      if (patient.previousReport?.id) {
        setSelectedReports((prev) =>
          [...prev, patient?.previousReport?.id].filter((id): id is string => id !== undefined)
        );
      }
    }
  };
  const downloadCount =
    selectedReports.length > 0 ? selectedReports.length : notDownloadedCount || 0;

  const isSelectAllDisabled = currentPatients.every(
    (p) => !p.previousReport || !p.previousReport.id
  );

  useEffect(() => {
    setSelectedPatientIds(new Set());
    setSelectedReports([]);
  }, [
    previousReportFilters,
    currentReportFilters,
    // Use serialized ID strings instead of object arrays — stable across renders
    // and only changes when the actual filter values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    effectiveClinics.map((c) => c.value).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    effectivePractitioners.map((p) => p.value).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    effectiveDeviceTypes.map((d) => d.value).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    effectiveVendors.map((v) => v.value).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    effectivePatientStatuses.map((s) => s.value).join(',')
  ]);

  // Page handlers
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  const handlePageLimitChange = (value: string | null) => {
    if (value) {
      const newLimit = parseInt(value, 10);
      dispatch(setPageLimit(newLimit));
      dispatch(setCurrentPage(1));
    }
  };

  const handleSearchChange = (value: string | undefined) => {
    dispatch(setSearchValue(value));
  };

  const handlePreviousReportFilterChange = (
    filters: string[],
    isRecentSigned?: boolean,
    isDownloaded?: boolean,
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE',
    isNonPractitionerSigned?: boolean
  ) => {
    if (hasCurrentFilters) {
      dispatch(clearCurrentReportFilters());
    }

    // Build the previous report filters object
    const newPreviousFilters: PreviousReportFilters = {};
    if (filters.length > 0) newPreviousFilters.colors = filters;
    if (isRecentSigned) {
      newPreviousFilters.isRecentSigned = isRecentSigned;
      // Only include recentSignedSortBy if isRecentSigned is true
      if (recentSignedSortBy) {
        newPreviousFilters.recentSignedSortBy = recentSignedSortBy;
      }
    }
    if (isDownloaded) newPreviousFilters.isDownloaded = isDownloaded;
    if (isNonPractitionerSigned) newPreviousFilters.isNonPractitionerSigned = true;

    dispatch(setPreviousReportFilters(newPreviousFilters));
  };

  const handleCurrentReportFilterChange = (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => {
    if (hasPreviousFilters) {
      dispatch(clearPreviousReportFilters());
    }

    // Build the current report filters object
    const newCurrentFilters: CurrentReportFilters = {};
    if (filters.length > 0) newCurrentFilters.colors = filters;
    if (isNonPractitionerSelected)
      newCurrentFilters.isNonPractitionerSigned = isNonPractitionerSelected;
    if (isPendingReviewSelected) newCurrentFilters.isPendingReview = isPendingReviewSelected;
    dispatch(setCurrentReportFilters(newCurrentFilters));
  };

  // Sort handler
  const handleSort = (field: string) => {
    let newDirection: SortDirection = 'ASC';

    if (sortBy === field) {
      // Cycling through: null -> ASC -> DESC -> null
      if (sortDirection === 'ASC') {
        newDirection = 'DESC';
      } else if (sortDirection === 'DESC') {
        newDirection = null;
        field = ''; // Clear sortBy when removing sort
      }
    }

    dispatch(
      setSort({
        sortBy: newDirection !== null ? field : '',
        sortDirection: newDirection
      })
    );
  };

  // Table rendering
  const renderTableBody = () => {
    if (error) {
      return (
        <Table.Tr>
          <Table.Td colSpan={100} p={0}>
            <Alert title="Error" color="red" px={20}>
              Error loading patients. Please refresh and try again
            </Alert>
          </Table.Td>
        </Table.Tr>
      );
    }

    if (!data?.patients || data.patients.length === 0) {
      return (
        <Table.Tr>
          <Table.Td colSpan={100} p={0}>
            <Center py={60}>
              <Box ta="center">
                <Text size="lg" fw={500} c="dimmed" mb={8}>
                  {hasActiveFilters ? 'No patients found' : 'No patients available'}
                </Text>
                <Text size="sm" c="dimmed">
                  {hasActiveFilters
                    ? 'Try adjusting your filters or search criteria'
                    : 'There are currently no active patients in the system'}
                </Text>
              </Box>
            </Center>
          </Table.Td>
        </Table.Tr>
      );
    }

    return currentPatients.map((patient: PatientData) => (
      <PatientTableRow
        key={patient.id}
        patient={patient}
        isSelected={selectedPatientIds.has(patient.id)}
        onToggle={handleTogglePatient}
        columns={isPatientListV2Enabled ? tableColumns : undefined}
        density={isPatientListV2Enabled ? density : undefined}
      />
    ));
  };

  const renderTable = () => {
    if (showTableLoading) {
      return (
        <TableSkeleton
          rows={pageLimit}
          columns={isPatientListV2Enabled ? tableColumns : undefined}
          density={isPatientListV2Enabled ? density : undefined}
        />
      );
    }

    return (
      <Table
        withTableBorder={!isPatientListV2Enabled}
        withColumnBorders={!isPatientListV2Enabled}
        className={isPatientListV2Enabled ? styles.tableV2 : undefined}>
        <TableHeader
          selectedPreviousReportFilters={previousReportFilters?.colors || []}
          selectedCurrentReportFilters={currentReportFilters?.colors || []}
          isNonPractitionerSignedCurrentReport={
            currentReportFilters?.isNonPractitionerSigned || false
          }
          isPendingReviewSelectedCurrentReport={currentReportFilters?.isPendingReview || false}
          isRecentSignedSelected={previousReportFilters?.isRecentSigned || false}
          isDownloadedSelected={previousReportFilters?.isDownloaded || false}
          onPreviousReportFiltersChange={handlePreviousReportFilterChange}
          onCurrentReportFiltersChange={handleCurrentReportFilterChange}
          isLoading={showTableLoading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          onSelectAll={handleSelectAll}
          isSelectAllDisabled={isSelectAllDisabled}
          columns={isPatientListV2Enabled ? tableColumns : undefined}
          density={isPatientListV2Enabled ? density : undefined}
        />
        <Table.Tbody className={styles.tableBody}>{renderTableBody()}</Table.Tbody>
      </Table>
    );
  };

  return (
    <div
      className={[isPatientListV2Enabled ? pocStyles.pocScope : undefined, styles.page]
        .filter(Boolean)
        .join(' ')}
      style={{
        display: 'grid',
        // v2 header renders two rows (title row + Filters/Columns row).
        gridTemplateRows: isPatientListV2Enabled ? 'auto auto 1fr auto' : 'auto 1fr auto',
        gridTemplateColumns: 'minmax(0, 1fr)',
        padding: '0 20px 16px'
      }}>
      <PageHeader
        patientCount={data?.total}
        isLoading={isLoading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        notDownloadedCount={notDownloadedCount}
        isNotDownloadedLoading={isNotDownloadedLoading}
        onNotDownloadedClick={onDownloadAllNotDownloadedReports}
        currentReportFilters={currentReportFilters}
        previousReportFilters={previousReportFilters}
        onCurrentReportFilterChange={handleCurrentReportFilterChange}
        onPreviousReportFilterChange={handlePreviousReportFilterChange}
        isPatientListV2Enabled={isPatientListV2Enabled}
        showEnrollmentMode={isPatientListV2Enabled && isInternalUser}
        enrollmentMode={enrollmentMode}
        onEnrollmentModeChange={setEnrollmentMode}
        selectedReportsCount={selectedReports.length > 0 ? selectedReports.length : undefined}
        isPatientCreateEnabled={isPatientCreateEnabled}
        onAddPatient={() => setIsCreatePatientOpen(true)}
        filtersButton={
          isPatientListV2Enabled ? (
            <FiltersButton
              selectedCurrentReportFilters={currentReportFilters?.colors ?? []}
              selectedPreviousReportFilters={previousReportFilters?.colors ?? []}
              isNonPractitionerSignedCRSelected={
                previousReportFilters?.isNonPractitionerSigned ?? false
              }
              isPendingReviewSelectedCRSelected={currentReportFilters?.isPendingReview ?? false}
              isRecentSignedSelected={previousReportFilters?.isRecentSigned ?? false}
              isDownloadedSelected={previousReportFilters?.isDownloaded ?? false}
              currentReportType={
                currentReportFilters && Object.keys(currentReportFilters).length > 0
                  ? 'current'
                  : previousReportFilters && Object.keys(previousReportFilters).length > 0
                    ? 'previous'
                    : null
              }
              recentSignedSortBy={
                (previousReportFilters as PreviousReportFilters)?.recentSignedSortBy ??
                'SIGNED_DATE'
              }
              onApplyReportFilters={(
                filters,
                isNonPractitioner,
                isPendingReview,
                isRecentSigned,
                isNotDownloaded,
                reportType,
                recentSignedSortByValue
              ) => {
                if (reportType === 'previous') {
                  handlePreviousReportFilterChange(
                    filters,
                    isRecentSigned,
                    isNotDownloaded,
                    recentSignedSortByValue,
                    isNonPractitioner
                  );
                } else {
                  handleCurrentReportFilterChange(filters, false, isPendingReview);
                }
              }}
            />
          ) : undefined
        }
        columnsButton={
          isPatientListV2Enabled ? (
            <ColumnsButton
              columns={columns}
              density={density}
              onVisibilityChange={setColumnVisibility}
              onReorder={reorderColumns}
              onDensityChange={setDensity}
            />
          ) : undefined
        }
      />

      {isCreatePatientOpen && (
        <CreatePatientModal
          isOpen={isCreatePatientOpen}
          onClose={() => setIsCreatePatientOpen(false)}
        />
      )}

      <div className={isPatientListV2Enabled ? styles.tableContainer : styles.tableContainerV1}>
        {renderTable()}
      </div>

      <div className={isPatientListV2Enabled ? styles.footer : styles.footerV1}>
        {data && data.total > 0 ? (
          <ListPager
            page={currentPage}
            pageSize={pageLimit}
            total={data.total}
            pageSizes={PAGE_SIZE_OPTIONS}
            onPageChange={handlePageChange}
            onPageSizeChange={(next) => handlePageLimitChange(String(next))}
          />
        ) : null}
      </div>

      <AsyncBatchDownloadModal
        batchDownloadState={batchState}
        setBatchDownloadState={setBatchState}
        onDownload={handleBatchDownload}
        onClose={handleClose}
        notDownloadedCount={downloadCount}
        title={`${downloadCount} Reports`}
        downloadingMessage={`Preparing ${downloadCount} reports for download...`}
        successMessage={`${downloadCount} reports are ready for download!`}
      />
    </div>
  );
};

export default PatientListPage;
